import { LoadingButton } from "@mui/lab";
import { Box, Checkbox, Dialog, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { acceptSPARTermsHandle } from "src/api/megs-api-api";
import logger from "src/utils/logger";
// Redux selectors - single source of truth for auth state
import {
  selectIsAuthenticated,
  selectUser,
  selectHasSparRole,
} from "src/redux/authSlice";

function SPARTermsAndConditionsModal({ open, handleClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [fullName, setFullName] = useState("");
  const [termsChecked, setTermsChecked] = useState(false);
  const [videoWatchedChecked, setVideoWatchedChecked] = useState(false);

  // Use Redux selectors - single source of truth for auth state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const hasSparRole = useSelector(selectHasSparRole);

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Don't render if user doesn't have SPAR role
  if (!hasSparRole) {
    return null;
  }

  const username = user?.username;

  const modalContent = [
    {
      heading: "Objective",
      body: [
        {
          text: "To ensure that all RICA agents comply with regulatory requirements, focusing on the 'do's and don'ts' necessary for legal and operational compliance. This document is not a substitute for full sales agent training, which remains mandatory.",
        },
      ],
    },
    {
      heading: "Key Responsibilities",
      body: [
        {
          number: "1",
          text: "Customer Verification",
          subpoints: [
            {
              letter: "a",
              text: "Verify all customers' identity and address details as per RICA requirements.",
            },
            {
              letter: "b",
              text: "If automatic verification fails, request the required documents (ID and proof of address).",
            },
            {
              letter: "c",
              text: "Maintain the integrity of the verification process—no shortcuts or falsification.",
            },
            {
              letter: "d",
              text: "Explicitly inform the customer that RICA registration includes consent for marketing communications via SMS, WhatsApp, email, and push notifications.",
            },
          ],
        },
        {
          number: "2",
          text: "Use of RICA Systems",
          subpoints: [
            {
              letter: "a",
              text: "Only use the SPAR Mobile Portal for RICA registrations.",
            },
            {
              letter: "b",
              text: "Self-RICA via WhatsApp (063 901 0001) is not within the agent’s responsibilities for onboarding customers.",
            },
            {
              letter: "c",
              text: "Agents may guide customers on how to use Self-RICA only if the customer explicitly requests it or prefers not to complete RICA in-store.",
            },
            {
              letter: "d",
              text: "Agents must never initiate Self-RICA on their own devices or facilitate the process beyond providing guidance.",
            },
          ],
        },
        {
          number: "3",
          text: "Compliance with Regulations",
          subpoints: [
            {
              letter: "a",
              text: "Do not proceed with any sale or SIM registration unless all RICA requirements are fully met.",
            },
            {
              letter: "b",
              text: "Avoid any actions that could contravene the RICA Act.",
            },
          ],
        },
        {
          number: "4",
          text: "Confidentiality",
          subpoints: [
            {
              letter: "a",
              text: "Keep all customer information confidential.",
            },
            {
              letter: "b",
              text: "Use customer data strictly for RICA compliance purposes only.",
            },
          ],
        },
        {
          number: "5",
          text: "Prohibited Actions",
          subpoints: [
            {
              letter: "a",
              text: "Do not register SIM cards without verifying the required customer information.",
            },
            {
              letter: "b",
              text: "Do not misrepresent your role or offer unauthorized products or services.",
            },
            {
              letter: "c",
              text: "Do not misuse RICA systems or compromise data integrity.",
            },
            {
              letter: "d",
              text: "Do not initiate or conduct Self-RICA on behalf of customers using the agent's own device.",
            },
          ],
        },
      ],
    },
    {
      heading: "Important Reminders",
      body: [
        {
          text: "Non-Compliance Risks: Failure to comply may lead to termination of your RICA agent status and potential legal consequences.",
        },
        {
          text: "Support Available: For any challenges or questions, reach out to customer care via WhatsApp at 063 901 0000",
        },
      ],
    },
  ];

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      const response = await acceptSPARTermsHandle(fullName, username);
      if (response.status_code === 1001) {
        localStorage.setItem("spar_terms_and_conditions_date", new Date().toISOString());
        handleClose();
      }
    } catch (error) {
      logger.error("Error accepting SPAR terms", error);
    }
    setIsLoading(false);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.90)" },
      }}
    >
      <DialogTitle>RICA Training for Agents</DialogTitle>
      <DialogContent
        dividers
        sx={{
          maxHeight: "700px",
          overflowY: "auto",
          ml: 0.5,
        }}
      >
        <Box>
          {modalContent.map((section, index) => (
            <Box key={index} sx={{ mt: index > 0 ? 2 : 0 }}>
              <Typography variant="h6">{section.heading}</Typography>
              {section.body.map((item, idx) => (
                <Box key={idx} sx={{ mt: idx > 0 ? 0.75 : 0 }}>
                  <Typography variant={item.subpoints ? "subtitle2" : "body2"} sx={{ ml: 2, display: "flex", gap: 1, mt: 1 }}>
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      {item.number}
                    </Box>
                    {item.text}
                  </Typography>
                  {item.subpoints &&
                    item.subpoints.map((sub, subIdx) => (
                      <Typography key={subIdx} variant="body2" sx={{ ml: 4, display: "flex", gap: 1, mt: 0.5 }}>
                        <Box component="span" sx={{ fontWeight: "bold" }}>
                          {sub.letter}.
                        </Box>
                        {sub.text}
                      </Typography>
                    ))}
                </Box>
              ))}
            </Box>
          ))}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" sx={{ mt: 2 }}>
              By proceeding with the RICA registration process on the SPAR Mobile Portal, you acknowledge and accept these compliance responsibilities and
              commit to adhering to the regulatory requirements.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Training Video
            </Typography>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "900px",
                paddingTop: "56.25%", // 16:9 aspect ratio
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/4Ws4pm4XrFk"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
               />
            </Box>
            <FormControlLabel
              control={<Checkbox color="primary" checked={videoWatchedChecked} onChange={(e) => setVideoWatchedChecked(e.target.checked)} />}
              label="I confirm that I have watched the training video above"
              sx={{ mt: 4 }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
            <FormControlLabel
              control={<Checkbox color="primary" checked={termsChecked} onChange={(e) => setTermsChecked(e.target.checked)} />}
              label={<Typography variant="body2">I have read and agree to these terms.</Typography>}
              sx={{ mt: 2 }}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
          <>
            <TextField label="Enter Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} sx={{ width: "70%", mt: 2 }} />
            <LoadingButton
              loading={isLoading}
              variant="contained"
              color="primary"
              sx={{ width: "33%", mt: 4 }}
              onClick={handleAccept}
              disabled={!fullName || !termsChecked || !videoWatchedChecked}
            >
              Accept
            </LoadingButton>
          </>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default SPARTermsAndConditionsModal;
